import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey, type ApiClientError } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';

/*
 * 세금계산서 관리(order PR #25) — 고도몰 화면 3개(설정·발행 요청 리스트·발행 내역 리스트)를 옮긴다.
 * Toss PG 관리자에는 세금계산서 기능·API 가 없어 백오피스에서 관리한다. 1차는 "일반 세금계산서"(운영자가 홈택스에서
 * 발행한 뒤 기록)만 — 전자세금계산서는 2차(electronicEnabled=true 는 400).
 *
 * BE `/api/admin/v1/orders/tax-invoices/**` — BO 는 게이트웨이 `/admin/v1/orders/**` 경유. 쓰기는 Idempotency-Key.
 * 조회 OPS_ADMIN·CS_AGENT·SUPER_ADMIN·ORDER_VIEW / 쓰기 OPS_ADMIN·CS_AGENT·SUPER_ADMIN / 설정 저장 OPS_ADMIN·SUPER_ADMIN.
 * 목록 페이지 정보는 envelope meta.pagination(page 는 1부터, totalItems).
 */

export const TAX_INVOICE_READ_ROLES = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'] as const;
export const TAX_INVOICE_WRITE_ROLES = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'] as const;
export const TAX_INVOICE_SETTINGS_WRITE_ROLES = ['OPS_ADMIN', 'SUPER_ADMIN'] as const;

const BASE = '/admin/v1/orders/tax-invoices';

// ── 타입 ─────────────────────────────────────────────────────────────

export interface Supplier {
  companyName: string;
  businessNo: string;
  ceoName: string;
  address: string;
  businessType: string;
  businessItem: string;
}

export type IssueDateBasis = 'PAYMENT' | 'DELIVERY';

export interface TaxInvoiceSettings {
  useEnabled: boolean;
  generalEnabled: boolean;
  electronicEnabled: boolean;
  requestLimitEnabled: boolean;
  requestLimitDay: number;
  includeShipping: boolean;
  includeMileage: boolean;
  includeDeposit: boolean;
  issueDateBasis: IssueDateBasis;
  showOnCheckout: boolean;
  guideEnabled: boolean;
  guideText: string;
  deadlineGuideEnabled: boolean;
  deadlineGuideText: string;
  supplier: Supplier;
}

export interface TaxInvoiceSettingsView extends TaxInvoiceSettings {
  updatedAt: string | null;
  updatedBy: string | null;
}

export type KeywordType = 'ALL' | 'COMPANY' | 'CEO' | 'REQUESTER' | 'ORDER_NO';
export type TaxType = 'ALL' | 'TAXABLE' | 'FREE';

export interface TaxInvoiceSearch {
  keywordType: KeywordType;
  keyword: string;
  businessNo: string;
  from: string;
  to: string;
  orderStatuses: string[];
  taxType: TaxType;
}

export interface IssueSearch extends TaxInvoiceSearch {
  dateType: 'ISSUE_DATE' | 'REQUEST_DATE';
  includeCanceled: boolean;
}

export interface BusinessInfo {
  companyName: string;
  businessNo: string;
  ceoName: string;
  businessType: string;
  businessItem: string;
  email: string;
  zipcode: string;
  address: string;
  addressDetail: string;
}

export interface RequestRow {
  requestId: string;
  orderId: string;
  orderNo: string;
  ordererName: string;
  orderStatus: string;
  paymentMethod: string | null;
  status: string;
  source: string | null;
  requestedAt: string | null;
  companyName: string;
  businessNo: string;
  ceoName: string;
  businessType: string;
  businessItem: string;
  email: string;
  address: string;
  payableAmount: number;
  /** TAXABLE · FREE · MIXED */
  taxType: string;
  expectedTaxableSupply: number;
  expectedTaxableVat: number;
  expectedTaxFree: number;
  adminMemo: string;
}

export interface IssueView {
  issueId: string;
  /** TAX_INVOICE(세금계산서·과세분) · INVOICE(계산서·면세분) */
  invoiceKind: string;
  /** GENERAL · ELECTRONIC */
  issueType: string;
  status: string;
  supplyAmount: number;
  vatAmount: number;
  totalAmount: number;
  issueDate: string | null;
  approvalNo: string | null;
}

export interface IssueResult {
  requestId: string;
  issued: boolean;
  reason: string | null;
  issues: IssueView[];
}

export interface IssueResultResponse {
  issuedCount: number;
  skippedCount: number;
  results: IssueResult[];
}

export interface IssueRow {
  issueId: string;
  requestId: string;
  orderId: string;
  orderNo: string;
  ordererName: string;
  orderStatus: string;
  requestedAt: string | null;
  invoiceKind: string;
  issueType: string;
  status: string;
  supplyAmount: number;
  vatAmount: number;
  totalAmount: number;
  issueDate: string | null;
  approvalNo: string | null;
  companyName: string;
  businessNo: string;
  ceoName: string;
  issuedBy: string | null;
  printedAt: string | null;
  canceledAt: string | null;
}

export interface IssueSummary {
  invoiceKind: string;
  issueType: string;
  companyCount: number;
  issueCount: number;
  totalAmount: number;
  supplyAmount: number;
  vatAmount: number;
}

export interface PrintBuyer {
  companyName: string;
  businessNo: string;
  ceoName: string;
  businessType: string;
  businessItem: string;
  email: string;
  address: string;
  addressDetail: string;
}

export interface PrintDocumentData {
  issueId: string;
  invoiceKind: string;
  orderNo: string;
  issueDate: string | null;
  approvalNo: string | null;
  buyer: PrintBuyer;
  itemSummary: string;
  supplyAmount: number;
  vatAmount: number;
  totalAmount: number;
}

export interface PrintData {
  supplier: Supplier;
  documents: PrintDocumentData[];
}

export interface Page<T> {
  items: T[];
  total: number;
}

// ── 정규화 ───────────────────────────────────────────────────────────

type Rec = Record<string, unknown>;
const s = (v: unknown): string => (typeof v === 'string' ? v : '');
const sn = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const n = (v: unknown): number => (typeof v === 'number' ? v : 0);
const b = (v: unknown): boolean => v === true;

const normalizeSupplier = (r: Rec | null | undefined): Supplier => ({
  companyName: s(r?.companyName),
  businessNo: s(r?.businessNo),
  ceoName: s(r?.ceoName),
  address: s(r?.address),
  businessType: s(r?.businessType),
  businessItem: s(r?.businessItem),
});

const normalizeSettings = (r: Rec): TaxInvoiceSettingsView => ({
  useEnabled: b(r.useEnabled),
  generalEnabled: b(r.generalEnabled),
  electronicEnabled: b(r.electronicEnabled),
  requestLimitEnabled: b(r.requestLimitEnabled),
  requestLimitDay: typeof r.requestLimitDay === 'number' && r.requestLimitDay >= 1 ? r.requestLimitDay : 10,
  includeShipping: b(r.includeShipping),
  includeMileage: b(r.includeMileage),
  includeDeposit: b(r.includeDeposit),
  issueDateBasis: r.issueDateBasis === 'DELIVERY' ? 'DELIVERY' : 'PAYMENT',
  showOnCheckout: b(r.showOnCheckout),
  guideEnabled: b(r.guideEnabled),
  guideText: s(r.guideText),
  deadlineGuideEnabled: b(r.deadlineGuideEnabled),
  deadlineGuideText: s(r.deadlineGuideText),
  supplier: normalizeSupplier(r.supplier as Rec | null),
  updatedAt: sn(r.updatedAt),
  updatedBy: sn(r.updatedBy),
});

const normalizeIssueView = (r: Rec): IssueView => ({
  issueId: s(r.issueId),
  invoiceKind: s(r.invoiceKind),
  issueType: s(r.issueType),
  status: s(r.status),
  supplyAmount: n(r.supplyAmount),
  vatAmount: n(r.vatAmount),
  totalAmount: n(r.totalAmount),
  issueDate: sn(r.issueDate),
  approvalNo: sn(r.approvalNo),
});

const normalizeRequest = (r: Rec): RequestRow => ({
  requestId: s(r.requestId),
  orderId: s(r.orderId),
  orderNo: s(r.orderNo),
  ordererName: s(r.ordererName),
  orderStatus: s(r.orderStatus),
  paymentMethod: sn(r.paymentMethod),
  status: s(r.status),
  source: sn(r.source),
  requestedAt: sn(r.requestedAt),
  companyName: s(r.companyName),
  businessNo: s(r.businessNo),
  ceoName: s(r.ceoName),
  businessType: s(r.businessType),
  businessItem: s(r.businessItem),
  email: s(r.email),
  address: s(r.address),
  payableAmount: n(r.payableAmount),
  taxType: s(r.taxType),
  expectedTaxableSupply: n(r.expectedTaxableSupply),
  expectedTaxableVat: n(r.expectedTaxableVat),
  expectedTaxFree: n(r.expectedTaxFree),
  adminMemo: s(r.adminMemo),
});

const normalizeIssueRow = (r: Rec): IssueRow => ({
  issueId: s(r.issueId),
  requestId: s(r.requestId),
  orderId: s(r.orderId),
  orderNo: s(r.orderNo),
  ordererName: s(r.ordererName),
  orderStatus: s(r.orderStatus),
  requestedAt: sn(r.requestedAt),
  invoiceKind: s(r.invoiceKind),
  issueType: s(r.issueType),
  status: s(r.status),
  supplyAmount: n(r.supplyAmount),
  vatAmount: n(r.vatAmount),
  totalAmount: n(r.totalAmount),
  issueDate: sn(r.issueDate),
  approvalNo: sn(r.approvalNo),
  companyName: s(r.companyName),
  businessNo: s(r.businessNo),
  ceoName: s(r.ceoName),
  issuedBy: sn(r.issuedBy),
  printedAt: sn(r.printedAt),
  canceledAt: sn(r.canceledAt),
});

const normalizeSummary = (r: Rec): IssueSummary => ({
  invoiceKind: s(r.invoiceKind),
  issueType: s(r.issueType),
  companyCount: n(r.companyCount),
  issueCount: n(r.issueCount),
  totalAmount: n(r.totalAmount),
  supplyAmount: n(r.supplyAmount),
  vatAmount: n(r.vatAmount),
});

const normalizeBuyer = (r: Rec | null | undefined): PrintBuyer => ({
  companyName: s(r?.companyName),
  businessNo: s(r?.businessNo),
  ceoName: s(r?.ceoName),
  businessType: s(r?.businessType),
  businessItem: s(r?.businessItem),
  email: s(r?.email),
  address: s(r?.address),
  addressDetail: s(r?.addressDetail),
});

// ── fetch helpers ────────────────────────────────────────────────────

interface Envelope {
  data?: unknown;
  meta?: { pagination?: { totalItems?: number } | null } | null;
}

const readEnvelope = async (res: Response): Promise<Envelope> => {
  if (!res.ok) throw await errorFromResponse(res);
  return (await res.json()) as Envelope;
};

const write = async (method: 'POST' | 'PUT' | 'PATCH', path: string, body: unknown): Promise<unknown> => {
  const res = await adminFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor(method, path, body) },
    body: JSON.stringify(body),
  });
  const env = await readEnvelope(res);
  // 같은 본문을 다시 보내는 정상 동작(같은 행 재수정 등)이 첫 응답 재생으로 묻히지 않게 키를 푼다.
  releaseIdempotencyKey(method, path, body);
  return env.data;
};

/** 검색 조건 → 쿼리스트링. orderStatuses 는 반복 파라미터, from/to 는 yyyy-MM-dd. */
export const buildSearchQuery = (q: TaxInvoiceSearch | IssueSearch, page: number, size: number): string => {
  const qs = new URLSearchParams({ page: String(page), size: String(size) });
  if (q.keyword.trim()) {
    qs.set('keywordType', q.keywordType);
    qs.set('keyword', q.keyword.trim());
  }
  if (q.businessNo.trim()) qs.set('businessNo', q.businessNo.trim());
  if (q.from) qs.set('from', q.from);
  if (q.to) qs.set('to', q.to);
  q.orderStatuses.forEach((st) => qs.append('orderStatuses', st));
  if (q.taxType !== 'ALL') qs.set('taxType', q.taxType);
  if ('dateType' in q) {
    qs.set('dateType', q.dateType);
    if (q.includeCanceled) qs.set('includeCanceled', 'true');
  }
  return qs.toString();
};

// ── 설정 ─────────────────────────────────────────────────────────────

export const taxInvoiceSettingsKey = () => ['admin', 'tax-invoices', 'settings'] as const;

export const useTaxInvoiceSettingsQuery = () =>
  useQuery<TaxInvoiceSettingsView, ApiClientError>({
    queryKey: taxInvoiceSettingsKey(),
    queryFn: async () => normalizeSettings(((await readEnvelope(await adminFetch(`${BASE}/settings`))).data ?? {}) as Rec),
    retry: false,
  });

export const useSaveTaxInvoiceSettingsMutation = () => {
  const qc = useQueryClient();
  return useMutation<TaxInvoiceSettingsView, ApiClientError, TaxInvoiceSettings>({
    mutationFn: async (body) => normalizeSettings(((await write('PUT', `${BASE}/settings`, body)) ?? {}) as Rec),
    onSuccess: (data) => qc.setQueryData(taxInvoiceSettingsKey(), data),
  });
};

// ── 발행 요청 리스트 ─────────────────────────────────────────────────

const listKeyRoot = ['admin', 'tax-invoices'] as const;

export const fetchRequests = async (q: TaxInvoiceSearch, page: number, size: number): Promise<Page<RequestRow>> => {
  const env = await readEnvelope(await adminFetch(`${BASE}/requests?${buildSearchQuery(q, page, size)}`));
  const items = (((env.data as Rec | null)?.items as Rec[] | undefined) ?? []).map(normalizeRequest);
  return { items, total: env.meta?.pagination?.totalItems ?? items.length };
};

export const useTaxInvoiceRequestsQuery = (q: TaxInvoiceSearch, page: number, size: number) =>
  useQuery<Page<RequestRow>, ApiClientError>({
    queryKey: [...listKeyRoot, 'requests', q, page, size],
    queryFn: () => fetchRequests(q, page, size),
    placeholderData: keepPreviousData,
    retry: false,
  });

export const useTaxInvoiceRequestMutations = () => {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: listKeyRoot });
  const update = useMutation<unknown, ApiClientError, { requestId: string; businessInfo: BusinessInfo; adminMemo: string }>({
    mutationFn: ({ requestId, businessInfo, adminMemo }) =>
      write('PATCH', `${BASE}/requests/${encodeURIComponent(requestId)}`, { businessInfo, adminMemo }),
    onSuccess: refresh,
  });
  const remove = useMutation<number, ApiClientError, string[]>({
    mutationFn: async (ids) => n(((await write('POST', `${BASE}/requests/delete`, { ids })) as Rec | null)?.count),
    onSuccess: refresh,
  });
  const issue = useMutation<IssueResultResponse, ApiClientError, { requestIds: string[]; issueDate?: string; approvalNo?: string }>({
    mutationFn: async (body) => {
      const d = ((await write('POST', `${BASE}/requests/issue`, body)) ?? {}) as Rec;
      return {
        issuedCount: n(d.issuedCount),
        skippedCount: n(d.skippedCount),
        results: ((d.results as Rec[] | undefined) ?? []).map((r) => ({
          requestId: s(r.requestId),
          issued: b(r.issued),
          reason: sn(r.reason),
          issues: ((r.issues as Rec[] | undefined) ?? []).map(normalizeIssueView),
        })),
      };
    },
    onSuccess: refresh,
  });
  return { update, remove, issue };
};

// ── 발행 내역 리스트 ─────────────────────────────────────────────────

export const fetchIssues = async (
  q: IssueSearch,
  page: number,
  size: number,
): Promise<Page<IssueRow> & { summary: IssueSummary[] }> => {
  const env = await readEnvelope(await adminFetch(`${BASE}/issues?${buildSearchQuery(q, page, size)}`));
  const d = (env.data ?? {}) as Rec;
  const items = ((d.items as Rec[] | undefined) ?? []).map(normalizeIssueRow);
  return {
    items,
    summary: ((d.summary as Rec[] | undefined) ?? []).map(normalizeSummary),
    total: env.meta?.pagination?.totalItems ?? items.length,
  };
};

export const useTaxInvoiceIssuesQuery = (q: IssueSearch, page: number, size: number) =>
  useQuery<Page<IssueRow> & { summary: IssueSummary[] }, ApiClientError>({
    queryKey: [...listKeyRoot, 'issues', q, page, size],
    queryFn: () => fetchIssues(q, page, size),
    placeholderData: keepPreviousData,
    retry: false,
  });

export const useTaxInvoiceIssueMutations = () => {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: listKeyRoot });
  const cancel = useMutation<number, ApiClientError, string[]>({
    mutationFn: async (ids) => n(((await write('POST', `${BASE}/issues/cancel`, { ids })) as Rec | null)?.count),
    onSuccess: refresh,
  });
  const print = useMutation<PrintData, ApiClientError, string[]>({
    mutationFn: async (ids) => {
      const d = ((await write('POST', `${BASE}/issues/print`, { ids })) ?? {}) as Rec;
      return {
        supplier: normalizeSupplier(d.supplier as Rec | null),
        documents: ((d.documents as Rec[] | undefined) ?? []).map((doc) => ({
          issueId: s(doc.issueId),
          invoiceKind: s(doc.invoiceKind),
          orderNo: s(doc.orderNo),
          issueDate: sn(doc.issueDate),
          approvalNo: sn(doc.approvalNo),
          buyer: normalizeBuyer(doc.buyer as Rec | null),
          itemSummary: s(doc.itemSummary),
          supplyAmount: n(doc.supplyAmount),
          vatAmount: n(doc.vatAmount),
          totalAmount: n(doc.totalAmount),
        })),
      };
    },
    // 인쇄하면 printedAt 이 찍힌다.
    onSuccess: refresh,
  });
  return { cancel, print };
};

/** 엑셀용 전체 조회 — BE size 최대 500 이라 페이지를 돌며 모은다. */
export const fetchAllPages = async <T>(fetchPage: (page: number, size: number) => Promise<Page<T>>): Promise<T[]> => {
  const SIZE = 500;
  const first = await fetchPage(0, SIZE);
  const all = [...first.items];
  const pages = Math.ceil(first.total / SIZE);
  for (let p = 1; p < pages; p++) all.push(...(await fetchPage(p, SIZE)).items);
  return all;
};

// ── 주문 단위 신청 정보(수정 폼 시드용) ──────────────────────────────
// 요청 리스트 행에는 우편번호·상세주소가 없어 행만으로 수정 폼을 채우면 저장 시 비워진다 —
// 주문별 조회(GET {orderId}/tax-invoice, FE 마이페이지와 같은 형태)의 request 로 폼을 채운다.
export const fetchOrderTaxInvoiceRequest = async (orderId: string): Promise<BusinessInfo | null> => {
  const env = await readEnvelope(await adminFetch(`/admin/v1/orders/${encodeURIComponent(orderId)}/tax-invoice`));
  const req = ((env.data ?? {}) as Rec).request as Rec | null | undefined;
  if (!req) return null;
  return {
    companyName: s(req.companyName),
    businessNo: s(req.businessNo),
    ceoName: s(req.ceoName),
    businessType: s(req.businessType),
    businessItem: s(req.businessItem),
    email: s(req.email),
    zipcode: s(req.zipcode),
    address: s(req.address),
    addressDetail: s(req.addressDetail),
  };
};
